/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { mapUrlToErrorCode, reportError } from '../utils/errorReporter'
import { getEmailConnectionAuthFailureFromResponse, getServerErrorMessage } from '../utils/get-user-error-message'
import { redactSecretLinks } from '../utils/secretLinkPage'
import { clearFailedTokenAndCheckBaseSurvives, getAuthToken, hasSessionOverride, markPendingRelocationNotice, stripEntityIdFromPath } from './authTokenStorage'

// Re-exported so every existing `import { X } from 'setup/SetupAxios'` keeps working — the
// implementations live in authTokenStorage.ts, which owns per-tab token storage as its own
// responsibility instead of being mixed into axios instance/interceptor wiring here.
export {
    AUTH_TOKEN,
    bearerTokenFrom,
    clearAuthToken,
    clearFailedTokenAndCheckBaseSurvives,
    clearLoggedOutFlag,
    clearSignupGates,
    decodeGoogleCredential,
    decodeJwtPayload,
    getAuthToken,
    hasSessionOverride,
    logoutAuthToken,
    setAuthToken,
    stripEntityIdFromPath,
    switchAuthToken,
} from './authTokenStorage'

const apiUrl = () => {
    const apiUrlParam = new URLSearchParams(window.location.search).get('apiUrl')
    if (apiUrlParam) {
        return apiUrlParam
    } else {
        if (process.env.REACT_APP_API_HOST?.startsWith('https://') || process.env.REACT_APP_API_HOST?.startsWith('http://')) {
            return process.env.REACT_APP_API_HOST
        }
        const protocol = window.location.protocol === 'https:' ? 'https' : 'http'
        const apiUrl = `${protocol}://${process.env.REACT_APP_API_HOST}`
        return apiUrl
    }
}

export const getApiBaseUrl = () => `${apiUrl()}/v1`

/** The API host WITHOUT the trailing `/v1` — for endpoints on the new routing convention
 *  (`/<domain>/<category>/<resource>/vN`), which mount at the root rather than under `/v1`. Single source
 *  of truth so box-truck / TMS-onboarding / instant-pay don't each re-derive the same strip. */
export const getApiBaseUrlWithoutV1 = () => getApiBaseUrl().replace(/\/v1$/, '')

const loadsIngestServiceURL = () => {
    if (process.env.REACT_APP_LOADS_INGEST_SERVICE_HOST?.startsWith('https://') || process.env.REACT_APP_LOADS_INGEST_SERVICE_HOST?.startsWith('http://')) {
        return process.env.REACT_APP_LOADS_INGEST_SERVICE_HOST
    }
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http'
    const host = process.env.REACT_APP_LOADS_INGEST_SERVICE_HOST

    return `${protocol}://${host}/v1`
}

/** The loads-ingest host WITHOUT the trailing `/v1` — for routes mounted at the service root, e.g.
 *  Market Intel (`/market-intel/.../vN`, ADR-033 §4.6). Pass it as a per-request `baseURL` on
 *  `loadsIngestServiceAxios` so the JWT interceptor still applies. */
export const getLoadsIngestServiceRootUrl = (): string => (loadsIngestServiceURL() ?? '').replace(/\/v1\/?$/, '')

export const loadsIngestServiceAxios = axios.create({
    baseURL: loadsIngestServiceURL(),
    headers: {
        Accept: 'application/json',
    },
})

/**
 * The load-search-service root, where the search stream lives (`SEARCH_STREAM_PATH`). Null when this build has no host
 * for it, so a user switched onto the stream by `featureFlags.boxTruckStreamSearch` on a build that cannot reach it
 * keeps `search-unified` instead of a broken board.
 */
export const getLoadSearchServiceUrl = (): string | null => {
    const host = process.env.REACT_APP_LOAD_SEARCH_SERVICE_HOST
    if (!host) return null
    if (host.startsWith('https://') || host.startsWith('http://')) return host.replace(/\/$/, '')
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http'
    return `${protocol}://${host}`
}

const generalAgentServiceURL = () => {
    if (process.env.REACT_APP_GENERAL_AGENT_SERVICE_HOST?.startsWith('https://') || process.env.REACT_APP_GENERAL_AGENT_SERVICE_HOST?.startsWith('http://')) {
        return process.env.REACT_APP_GENERAL_AGENT_SERVICE_HOST
    }
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http'
    const host = process.env.REACT_APP_GENERAL_AGENT_SERVICE_HOST

    return `${protocol}://${host}/v1`
}

export const generalAgentServiceAxios = axios.create({
    baseURL: generalAgentServiceURL(),
    headers: {
        Accept: 'application/json',
        // Identify the admin panel as the WEB client so GAS stamps `chat_variant='web'` on tab
        // creation (the mobile app sends 'mobile') — keeps web tabs in the web bar, mobile tabs
        // in the mobile drawer. See general-agent-service tabs controller (x-numeo-client).
    },
})

const socketServiceURL = () => {
    // Reuse the WS URL env var — socket-service exposes both WebSocket and HTTP endpoints.
    if (process.env.REACT_APP_SOCKET_SERVICE_URL?.startsWith('https://') || process.env.REACT_APP_SOCKET_SERVICE_URL?.startsWith('http://')) {
        return process.env.REACT_APP_SOCKET_SERVICE_URL
    }
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http'
    const host = process.env.REACT_APP_SOCKET_SERVICE_URL
    return `${protocol}://${host}`
}

// HTTP client for socket-service endpoints (e.g. Agentic Hub event replay on reconnect).
export const socketServiceAxios = axios.create({
    baseURL: socketServiceURL(),
    headers: {
        Accept: 'application/json',
    },
})

// Apply the same interceptors to loadsIngestAxios
const setupLoadsIngestServiceAxios = () => {
    loadsIngestServiceAxios.interceptors.request.use(
        (config: any) => {
            const accessToken = getAuthToken()
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`
            }

            // Add release date to headers if available
            if (process.env.REACT_APP_RELEASE_DATE) {
                config.headers['Release-Date'] = process.env.REACT_APP_RELEASE_DATE
            }

            return config
        },
        (err: any) => Promise.reject(err)
    )
}

const setupGeneralAgentServiceAxios = () => {
    generalAgentServiceAxios.interceptors.request.use(
        (config: any) => {
            const accessToken = getAuthToken()
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`
            }

            if (process.env.REACT_APP_RELEASE_DATE) {
                config.headers['Release-Date'] = process.env.REACT_APP_RELEASE_DATE
            }

            return config
        },
        (err: any) => Promise.reject(err)
    )
}

// Shared 401-recovery core for every axios instance (default, socket-service, ...): falls back to
// the shared base session when only this tab's per-tab override was invalidated, or signs out
// entirely when the base session itself is gone. Pulled out so the two instances' interceptors
// can't drift the way this comment used to warn about ("Align behavior with the default instance").
function recoverFromUnauthorized(err: AxiosError, on401?: () => void) {
    // Read BEFORE clearFailedTokenAndCheckBaseSurvives — it clears the override as part of
    // recovering, so checking after would always see "no override" regardless of whether this tab
    // actually had one. Only a genuinely switched tab falling back counts as a "relocation" worth
    // surfacing; a base-session tab's own token expiring and re-resolving is normal, silent 401
    // recovery, not a move to a different company.
    const wasOverride = hasSessionOverride()
    if (clearFailedTokenAndCheckBaseSurvives(err)) {
        // Only this tab's override was invalid and the shared base session still resolves — fall
        // back to it instead of forcing a full sign-out. replace() + stripEntityIdFromPath(), not
        // a bare reload(): the tab may be on a company-scoped entity page (e.g.
        // /loads/<companyB-objectId>) that 404s once it reloads under the base company's data —
        // the same reason the switch flows themselves strip entity IDs before navigating.
        if (wasOverride) markPendingRelocationNotice()
        window.location.replace(stripEntityIdFromPath())
    } else {
        localStorage.removeItem('qr_session_id')
        window.location.href = '/auth/sign-in'
        on401?.()
    }
}

const setupSocketServiceAxios = () => {
    socketServiceAxios.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            const accessToken = getAuthToken()
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`
            }
            return config
        },
        (err: AxiosError) => Promise.reject(err)
    )

    // Response interceptor: surface 401s the same way the main axios instance does. Without this,
    // an expired JWT during replay would fail silently (the replay handler swallows errors by
    // design), leaving the user authenticated at the UI level but unable to fetch anything.
    // Align behavior with the default instance so the user is redirected to sign-in cleanly.
    socketServiceAxios.interceptors.response.use(
        (res: AxiosResponse) => res,
        (err: AxiosError) => {
            const status = err?.status ?? err?.response?.status
            const currentPath = window.location.pathname
            const isOnExtPage =
                currentPath.startsWith('/ext-v2') || currentPath === '/ext' || currentPath.startsWith('/ext/') || currentPath.startsWith('/extension/')
            const isOnTwaPage = currentPath.startsWith('/twa/')
            if (status === 401 && !isOnExtPage && !isOnTwaPage) {
                recoverFromUnauthorized(err)
            }
            return Promise.reject(err)
        }
    )
}

export default function setupAxios(on401?: () => void) {
    console.log(`SetupAxios::API_URL :::: ${apiUrl()}`)
    axios.defaults.baseURL = `${apiUrl()}/v1`
    axios.defaults.headers.Accept = 'application/json'

    // Also setup the service-specific axios instances
    setupLoadsIngestServiceAxios()
    setupGeneralAgentServiceAxios()
    setupSocketServiceAxios()
    axios.interceptors.request.use(
        (config: any) => {
            const url = config.url || ''
            const isPublicTracking = url.includes('/public/tracking') || url.includes('/public/agreements')

            // Don't add auth token for public tracking endpoints
            // Also don't overwrite if Authorization header is already explicitly set (e.g., by /ext/* pages using loadops_auth_data)
            if (!isPublicTracking && !config.headers.Authorization) {
                const accessToken = getAuthToken()
                if (accessToken) {
                    config.headers.Authorization = `Bearer ${accessToken}`
                }
            }

            // Add release date to headers if available
            if (process.env.REACT_APP_RELEASE_DATE) {
                config.headers['Release-Date'] = process.env.REACT_APP_RELEASE_DATE
            }

            return config
        },
        (err: any) => Promise.reject(err)
    )
    axios.interceptors.response.use(
        (res: AxiosResponse) => res,
        (err: AxiosError) => {
            const status = err.status ?? err.response?.status
            const url = err.config?.url || ''
            const currentPath = window.location.pathname
            // A 401 carrying one of the email-service auth codes belongs to the connected mailbox,
            // not the user's Numeo session. Let the calling email surface open its reconnect flow.
            const isEmailConnectionAuthError = getEmailConnectionAuthFailureFromResponse(err.response?.data) !== null

            // Skip redirect for login requests, public tracking endpoints, and extension auth pages
            const isLoginRequest = url.includes('/auth/sign-in') || url.includes('/auth/sign-up') || url.includes('/check-email') || url.includes('/auth/qr/')
            const isPublicTracking = url.includes('/public/tracking') || url.includes('/public/agreements')
            const isExtAuthPage =
                url.includes('ext/signup') ||
                url.includes('ext/login') ||
                url.includes('ext/forgot-password') ||
                url.includes('ext/reset-password') ||
                url.includes('ext/resend-code')

            // Skip redirect if user is on any /ext/*, /ext-v2/*, or /extension/* page (public extension pages)
            const isOnExtPage =
                currentPath.startsWith('/ext-v2') || currentPath === '/ext' || currentPath.startsWith('/ext/') || currentPath.startsWith('/extension/')
            const isOnPublicPricingPage = currentPath.startsWith('/numeai-pricing')
            // Mobile pricing + success/cancel pages are token-authed via OTT in the URL,
            // not via session cookie. A 401 here should not redirect to sign-in.
            const isOnMobilePricingPage = currentPath.startsWith('/m/pricing') || currentPath.startsWith('/m/billing/')

            // /auth/hub and /auth/tms handle their own bad-token UX (renders the sign-in form with
            // an inline "invalid or expired" banner via the token-handoff verifyAuth() catch path).
            // Forcing a /auth/sign-in redirect here would short-circuit that fallback.
            const isOnAuthHubPage =
                currentPath === '/auth/hub' ||
                currentPath.startsWith('/auth/hub/') ||
                currentPath === '/auth/tms' ||
                currentPath.startsWith('/auth/tms/') ||
                currentPath.startsWith('/onboarding/tms/')

            // Skip redirect for factoring endpoints - 401 means provider auth is invalid, not user auth
            // /wex/ is the accounting sub-router mounted separately from /factoring/wex (see main-service app.ts:591)
            // /rts/ covers both /v1/rts/* (accounting) and /v1/integrations/rts/* (broker rating)
            const isFactoringEndpoint = url.includes('/factoring/') || url.includes('/rts/') || url.includes('/triumph/') || url.includes('/wex/')

            // TWA pages handle their own auth via Telegram initData — no redirect to sign-in
            const isOnTwaPage = currentPath.startsWith('/twa/')

            // The accounting/dispatch promo forms are public (linked from campaigns/the mobile
            // promo banner). A stale token here 401s on /auth/verify; bouncing to sign-in would
            // put back the exact wall these pages exist to avoid. The form just renders
            // un-prefilled instead.
            const isOnPromoPage = currentPath.startsWith('/promo-accounting') || currentPath.startsWith('/promo-dispatch')

            // The assistant connect page keeps its ?request= query across sign-in itself; the generic bounce to
            // /auth/sign-in would drop it and strand the assistant's sign-in.
            const isOnConnectPage = currentPath.startsWith('/connect/')

            if (
                status === 401 &&
                !isEmailConnectionAuthError &&
                !isLoginRequest &&
                !isPublicTracking &&
                !isExtAuthPage &&
                !isOnExtPage &&
                !isOnPublicPricingPage &&
                !isOnMobilePricingPage &&
                !isOnAuthHubPage &&
                !isFactoringEndpoint &&
                !isOnTwaPage &&
                !isOnPromoPage &&
                !isOnConnectPage
            ) {
                recoverFromUnauthorized(err, on401)
            }

            // Report 5xx server errors to the alerting system
            if (typeof status === 'number' && status >= 500 && !url.includes('/errors/report')) {
                reportError({
                    code: mapUrlToErrorCode(url),
                    errorMessage: getServerErrorMessage(err) || err.message || 'Server error',
                    severity: 'critical',
                    // A signing link's token is in its request address, and this report goes to the team's alert channel.
                    requestContext: { method: err.config?.method, url: redactSecretLinks(url), statusCode: status },
                })
            }

            return Promise.reject(err)
        }
    )
}
